import { useContext, useEffect } from 'react';

import { SocketContext } from '@/contexts/rtm';
import { useDeskId } from '@/hooks/useDeskId';
import { API as ruleAPI } from '@/modules/rules/hooks/useRules';
import { SocketEvent } from '@/shared/types/rtm';

import { bind, RtmContext, useRtmBindings } from './rtmBindings';

/** A reorder shifts the position of every rule after the moved one, so each is refreshed. */
const refreshAllRules = async (deskId: string, { rtm }: RtmContext) => {
  const { rules } = await ruleAPI.listRules(deskId);
  rules.forEach((rule) => rtm.update('rules', deskId, rule.rule_id));
};

/** Events delivered to the room of the selected desk (see `subscribe_desk` below). */
export const deskBindings = [
  bind(SocketEvent.business_hours_created, (data, { rtm }) =>
    rtm.create('business_hours', data.desk_id, data.business_hours_id)
  ),
  bind(SocketEvent.business_hours_updated, (data, { rtm }) =>
    rtm.update('business_hours', data.desk_id, data.business_hours_id)
  ),
  bind(SocketEvent.business_hours_removed, (data, { rtm }) =>
    rtm.remove('business_hours', data.desk_id, data.business_hours_id)
  ),

  bind(SocketEvent.bundles_created, (data, { rtm }) =>
    rtm.create('bundles', data.desk_id, data.bundle_id)
  ),
  bind(SocketEvent.bundles_updated, (data, { rtm }) =>
    rtm.update('bundles', data.desk_id, data.bundle_id)
  ),
  bind(SocketEvent.bundles_removed, (data, { rtm }) =>
    rtm.remove('bundles', data.desk_id, data.bundle_id)
  ),

  bind(SocketEvent.integrations_created, (data, { rtm }) =>
    rtm.create('integrations', data.desk_id, data.integration_id)
  ),
  bind(SocketEvent.integrations_updated, (data, { rtm }) =>
    rtm.update('integrations', data.desk_id, data.integration_id)
  ),
  bind(SocketEvent.integrations_removed, (data, { rtm }) =>
    rtm.remove('integrations', data.desk_id, data.integration_id)
  ),

  bind(SocketEvent.macros_created, (data, { rtm }) =>
    rtm.create('macros', data.desk_id, data.macro_id)
  ),
  bind(SocketEvent.macros_updated, (data, { rtm }) =>
    rtm.update('macros', data.desk_id, data.macro_id)
  ),
  bind(SocketEvent.macros_removed, (data, { rtm }) =>
    rtm.remove('macros', data.desk_id, data.macro_id)
  ),

  bind(SocketEvent.rules_created, (data, { rtm }) =>
    rtm.create('rules', data.desk_id, data.rule_id)
  ),
  bind(SocketEvent.rules_updated, (data, { rtm }) =>
    rtm.update('rules', data.desk_id, data.rule_id)
  ),
  bind(SocketEvent.rules_removed, (data, { rtm }) =>
    rtm.remove('rules', data.desk_id, data.rule_id)
  ),
  bind(SocketEvent.rules_reordered, (data, context) => refreshAllRules(data.desk_id, context)),
];

export const useRtmDesk = () => {
  const { socket, isConnected } = useContext(SocketContext);
  const desk_id = useDeskId();

  useRtmBindings(socket, deskBindings);

  useEffect(() => {
    if (!socket || !desk_id || !isConnected) {
      return;
    }
    socket.emit(SocketEvent.subscribe_desk, { desk_id });
    return () => {
      socket.emit(SocketEvent.unsubscribe_desk, { desk_id });
    };
  }, [isConnected, desk_id, socket]);
};

export default useRtmDesk;
